import React from 'react';
import { useNavigate } from 'react-router-dom';
import ProductPage from '../components/ProductPage';
import { UL101Technology } from '../components/UL101Technology';
import { AmmTaskFinder } from '../components/AmmTaskFinder';
import { UL101KitParts } from '../components/UL101KitParts';

const UL101Page = ({ onRequestDemo, onRequestQuote }) => {
  const navigate = useNavigate();
  const productData = {
    productName: 'CTRL UL101 Aviation Troubleshooter',
    metaDescription: 'Boeing SPL-1473 ultrasonic troubleshooter for leak, hydraulic and electrical faults. Intrinsically safe, 6-year warranty, kit parts and AMM tasks.',
    partNumber: 'B00033',
    boeingToolTag: 'SPL-1473',
    tagline: 'Acoustic Ultrasound Detection System for Precision Aviation Maintenance',
    description: 'The CTRL UL101 Aviation Troubleshooter is an industrial-grade ultrasonic detection system that assists technicians in rapidly pinpointing pneumatic, hydraulic, electrical, and mechanical faults in aircraft maintenance. Operating at 40 kHz, the UL101 converts ultrasonic activity into the audible range, providing an intuitive and non-destructive method for identifying issues that traditional methods often miss. This tool transforms time-consuming troubleshooting into pinpoint diagnostics. Many users report 100% ROI in the first or second use.',
    videoEmbed: 'https://player.vimeo.com/video/1185283512?badge=0&autopause=0&player_id=0&app_id=58479',
    videoAspect: 75,
    heroImage: '/images/img_0291.jpeg',
    dataSheetUrl: '/docs/ctrl-ul101-aviation-troubleshooter-barfield.pdf',
    boeingCoverage: {
      toolTag: 'SPL-1473',
      toolTagLabel: 'Boeing Special Tool',
      status: 'primary',
      summary:
        'The CTRL UL101 Aviation Troubleshooter (P/N B00033) IS Boeing tool tag SPL-1473 — the handheld ultrasonic leak-detection probe the Boeing AMM calls out for cabin pressurization, air-cycle-machine, hydraulic bypass, and pressurized-component leak checks.',
      boeingParts: [
        { pn: 'B00033', supplier: '62373 (CTRL Systems)', notes: 'CTRL UL101 Aviation Troubleshooter — SPL-1473' },
      ],
      tasks: [
        { number: '20-10-79-270-801', name: 'Ultrasonic Leak Detection Checks (cabin pressurization, A/C, bleed air, hydraulic bypass, parking brake, pitot-static, oxygen, tires & pressurized components)' },
        { number: '—', name: 'Intermittent Wiring — Inspection / Check' },
        { number: '—', name: 'Vacuum Tool Leak Check of the Door Seal (Fwd + Aft entry doors)' },
        { number: '—', name: 'Parking Brake Pressure Decrease — Fault Isolation (set + released)' },
        { number: '—', name: 'Doors, Hatches, & No. 2 Window — Leak Test' },
        { number: '—', name: 'Cabin Air Compressor (Health Check) — Leak Test' },
        { number: '—', name: 'Pack Cooling & Temperature Control System (CACTCS) — Operational Test' },
        { number: '—', name: 'Air Cycle Machine + Heat Exchanger + Bypass / Low-Limit / Economy Cooling Valve — Installation' },
      ],
    },
    features: [
      {
        title: 'Ultrasonic Detection at 40 kHz',
        description: 'Detects 40 kHz ultrasonic activity and converts it into audible sound for precise fault identification'
      },
      {
        title: 'Industrial Grade Construction',
        description: 'Extruded aluminum housing (2.3mm wall thickness) built for demanding aviation environments'
      },
      {
        title: 'Intrinsically Safe',
        description: 'ANSI/UL 913-88 certified for Division 1 & 2 hazardous environments - safe for use around fuel systems'
      },
      {
        title: 'Extended 150-Foot Range',
        description: 'Industry-leading detection distance of 45 meters for large aircraft troubleshooting'
      },
      {
        title: 'High Sensitivity Threshold',
        description: 'Minimum intensity of 10⁻¹²W/m² (0 dB SPL) detects even the smallest leaks and anomalies'
      },
      {
        title: 'Six-Year Limited Warranty',
        description: 'Comprehensive parts and labor warranty demonstrates long-term reliability and manufacturer confidence'
      },
      {
        title: 'Extensive Probe Accessories',
        description: 'Concentrators and acoustic extension probes for airborne applications; solid probes for contact applications'
      },
      {
        title: 'Integrated UT200 Transmitter',
        description: 'Enables tightness testing and non-pressurized system diagnostics'
      },
      {
        title: 'Long Battery Life',
        description: '45+ hours continuous use on a single 9-volt alkaline battery'
      },
      {
        title: 'Wide Operating Range',
        description: 'Functions in extreme conditions from -20°F to 130°F (-4°C to 54°C)'
      }
    ],
    specifications: [
      { label: 'Part Number', value: 'B00033' },
      { label: 'Boeing Tool Tag', value: 'SPL-1473' },
      { label: 'Operating Frequency', value: '40 kHz ± 1.5 kHz' },
      { label: 'Frequency Bandwidth', value: '1.8-2.2 kHz @ -3 dB SPL' },
      { label: 'Detection Range', value: '150 feet (45 meters)' },
      { label: 'Sensitivity Threshold', value: '10⁻¹²W/m² (0 dB SPL)' },
      { label: 'Minimum Ultrasonic Pressure', value: '2.0 x 10⁻⁵ PA @ 40 kHz' },
      { label: 'Operating Temperature', value: '-20°F to 130°F (-4°C to 54°C)' },
      { label: 'Battery Life', value: '45+ hours continuous use (9V alkaline)' },
      { label: 'Receiver Dimensions', value: '8.75" x 1.26" x 1.26" (222 x 32 x 32 mm)' },
      { label: 'Receiver Weight', value: '11.6 oz (330 grams)' },
      { label: 'Transmitter Dimensions', value: '4.125" x 1.26" x 1.26" (105 x 32 x 32 mm)' },
      { label: 'Transmitter Weight', value: '6.2 oz (175 grams)' },
      { label: 'Housing Material', value: 'Industrial Grade Extruded Aluminum (2.3mm wall)' },
      { label: 'Headset Impedance', value: '600 Ohm with 21 dB noise attenuation' },
      { label: 'Intrinsic Safety', value: 'ANSI/UL 913-88 Division 1 & 2 Certified' },
      { label: 'Warranty', value: '6-Year Parts & Labor' },
      { label: 'Calibration', value: 'Biennial sensitivity validation required' }
    ],
    applications: [
      'Actuator Troubleshooting - Rapid diagnosis of actuator performance issues',
      'AC Packs Leak Detection - Identify air conditioning system leaks',
      'Bleed Air System Leaks - Detect engine and airframe bleed air leaks',
      'Canopy Pressure Testing - Verify seal integrity',
      'Cabin Pressure Leak Detection - Critical for pressurization system integrity',
      'Corona Discharge Identification - Locate electrical arcing sources',
      'Fuel Cell/Tank Leak Detection - Safety-critical fuel system inspection',
      'Hydraulic System Diagnostics - Actuators, lines, reservoirs, and switches',
      'Intermittent Wiring Inspection - Pinpoint elusive electrical faults',
      'Oxygen System Leak Detection - Essential for life support system integrity',
      'Parking Brake Pressure Testing - Verify hydraulic brake system integrity',
      'Pitot Static Inspection - Non-pressurized diagnostic capability',
      'Regulator Bypass Detection - Contact probe inspection of regulator performance',
      'Schrader Valve Inspection - Quick verification of valve integrity',
      'Vacuum Leak Detection - Window and door seal inspection',
      'Valve Bypass Detection - Contact probe identification of valve failures',
      'Landing Gear Nitrogen Leaks - Prevent unexpected gear collapse',
      'Engine Fuel Nozzle Inspection - CFM LEAP and other engine types',
      'Flap Control Valve Diagnostics - Prevent flap disagree conditions',
      'Bearing & Transmission Monitoring - Early detection of mechanical wear',
      'AOG Troubleshooting - Rapid fault isolation to minimize downtime'
    ],
    caseStudies: [
      {
        title: 'Boeing 737 MAX — CFM LEAP 1-B Fuel Nozzle Inspection',
        problem:
          'A Boeing 737 MAX CFM LEAP 1-B engine developed a recurring fuel nozzle anomaly. Traditional troubleshooting required borescope inspection and often led to blanket replacement of all 18 fuel nozzles — consuming hours of engine downtime and introducing contamination risk each time the combustor was opened.',
        solution:
          'Technicians deployed the UL101 Aviation Troubleshooter (B00033) to listen for the ultrasonic signature of internal leakage at each nozzle. Within minutes the single defective nozzle was isolated acoustically — without any borescope or intrusive disassembly.',
        results: [
          'Reduced diagnostic time to under 60 minutes',
          'Eliminated borescope contamination risk',
          'Identified the single failing nozzle vs. replacing all 18',
          'No special certification required for technicians',
          'Returned the engine to service the same shift',
        ],
        pdfs: [
          {
            label: 'Download Use Case PDF',
            title: 'CFM LEAP 1-B Fuel Nozzle Inspection, Boeing 737 MAX',
            url: '/docs/case-study-cfm-international-leap-1-b-fuel-nozzle-inspection-boeing-737-max.pdf',
          },
        ],
        videoUrl: 'https://vimeo.com/1184675327',
      },
      {
        title: 'Boeing 737-700 — Flap Control & Bypass Valves',
        problem:
          'Flaps on a Boeing 737-700 were slow to move, especially on retraction. The initial diagnosis called for replacing the flap control valve and solenoid — a time-intensive repair that threatened an overnight AOG.',
        solution:
          'A MOC technician used the UL101 with a solid probe to listen sequentially to the control valve and each bypass valve. A good valve produced no ultrasound; the faulty bypass valve was unmistakably loud. Targeted replacement of the single bypass valve resolved the fault.',
        results: [
          '$6,480 saved vs. control valve / solenoid replacement',
          '108% ROI on a single incident',
          'Troubleshoot time cut by 8 hours (11h → 3h)',
          'Prevented an overnight AOG — same-day return to service',
          'Aircraft released on the original dispatch schedule',
        ],
        pdfs: [
          {
            label: 'Download Use Case PDF',
            title: 'Flap Control and Bypass Valves, Boeing 737-700',
            url: '/docs/case-study-flap-control-and-bypass-valves-boeing-737-700.pdf',
          },
        ],
        videoUrl: 'https://vimeo.com/1184458958',
      },
      {
        title: 'Boeing 777-300ER — Nose Landing Gear Nitrogen Leak',
        problem:
          'A Boeing 777-300ER experienced multiple AOG events from persistent low nitrogen pressure in the nose landing gear strut. Traditional troubleshooting failed to locate the leak despite replacing the stabilizer trim pressure switch and associated plumbing. No visible fluid leaks were present, and the problem continued to recur.',
        solution:
          'The UL101 Aviation Troubleshooter was deployed to investigate the complete stabilizer trim pressure switch and plumbing system. At 40 kHz, the UL101 located an air leak inside the manifold assembly that had been undetectable by conventional methods.',
        results: [
          'Pinpointed a defective manifold assembly (faulty from stock)',
          'Problem that eluded traditional methods was resolved in minutes',
          'Aircraft returned to service, eliminating further AOG downtime',
          'Avoided costly dispatch of specialist landing-gear technicians',
          'Demonstrated 100% ROI on a single critical use',
        ],
        pdfs: [
          {
            label: 'Download Use Case PDF',
            title: 'Pinpointing Nitrogen Leaks in 777 NGL',
            url: '/docs/case-study-pinpointing-nitrogen-leaks-in-777-ngl-.pdf',
          },
        ],
        videoUrl: null,
      },
      {
        title: 'Flap Transmission Actuators — Boeing 737-700 (LE) & 757-200 (TE)',
        problem:
          'On a Boeing 737-700 AOG event, the leading-edge flap system called for all eight LE transmission actuators to be replaced — a 96-hour troubleshoot and six-figure parts bill. Separately, a Boeing 757-200 trailing-edge flap disagree fault had defied trial-and-error swaps across multiple transmissions.',
        solution:
          'Technicians used the UL101 with a solid contact probe to listen to each actuator and transmission for friction, internal leakage, blockages, and fluid bypass. Ultrasonic signatures isolated the specific defective components without removing surrounding assemblies.',
        results: [
          'LE flap 737-700: 96 → 36 hrs troubleshoot (60 hrs saved)',
          '$74,400 total savings vs. replacing all 8 LE actuators',
          'LE flap single-incident ROI: 1,078% on a single UL101',
          'TE flap 757-200: pinpointed #4 & #5 transmissions directly',
          'Detects friction, internal leakage, blockages & fluid bypass',
        ],
        pdfs: [
          {
            label: 'Download Leading Edge PDF',
            title: 'Leading Edge Flap Transmission Actuators, Boeing 737-700',
            url: '/docs/case-study-leading-edge-flap-transmission-actuators-boeing-737-700-2.pdf',
          },
          {
            label: 'Download Trailing Edge PDF',
            title: 'Trailing Edge Flap Disagree, Boeing 757-200',
            url: '/docs/case-study-trailing-edge-flap-disagree-boeing-757-200.pdf',
          },
        ],
        videoUrl: null,
      },
    ],
    benefits: [
      'Many users report 100% ROI in the first or second use',
      'Non-destructive testing allows inspection during normal operation',
      'Early detection of issues before catastrophic failure',
      'Pinpoints exact source due to rapid ultrasonic attenuation',
      'Differentiates individual components in complex systems',
      'Reduces aircraft downtime and maintenance costs',
      'Easy to learn and master - minimal training required',
      'Versatile across pneumatic, hydraulic, electrical, and mechanical systems',
      'Intrinsically safe for use in hazardous environments',
      'Instant, real-time feedback for immediate decision-making'
    ],
    relatedProducts: [
      {
        name: 'UL101 Compact Ultrasonic Inspection and Imaging Kit (B00705)',
        description: 'The complete UL101 kit plus the FOTRIC H4mini acoustic imaging camera — see the leak, then confirm it.',
        image: 'https://static.wixstatic.com/media/0fa391_949dee3c5b9842539c8efd8cf74acd56~mv2.png/v1/fill/w_720,h_720,al_c,q_85,enc_auto/0fa391_949dee3c5b9842539c8efd8cf74acd56~mv2.png',
        cta: 'View Kit',
        onClick: () => navigate('/products/combo-kits/b00705'),
      },
      {
        name: 'UT2000 Cabin Multi-Pak — Set of 4 (026305)',
        description: 'Four UT2000 transmitters in a NANUK 903 case for full-cabin pressurization leak checks.',
        image: '/parts/026305.webp',
        cta: 'View Part',
        onClick: () => navigate({ hash: '#part-026305' }),
      },
      {
        name: 'UT2000 Cabin Multi-Pak — Set of 8 (026310)',
        description: 'Eight UT2000 transmitters for wide-body cabins and simultaneous multi-zone checks.',
        image: '/parts/026310.webp',
        cta: 'View Part',
        onClick: () => navigate({ hash: '#part-026310' }),
      },
      {
        name: 'UT2000 Universal Transmitter — Replacement (026012)',
        description: 'Intrinsically safe replacement tone generator for the UL101 kit.',
        image: '/parts/026012.webp',
        cta: 'View Part',
        onClick: () => navigate({ hash: '#part-026012' }),
      },
      {
        name: 'Industrial Headset — Replacement (025020)',
        description: 'Noise-isolating over-the-head headset for the UL101 receiver.',
        image: '/parts/025020.webp',
        cta: 'View Part',
        onClick: () => navigate({ hash: '#part-025020' }),
      },
      {
        name: 'Headset Cable, mini-XLR — Replacement (025035)',
        description: 'Coiled mini-XLR cable connecting the headset to the UL101 receiver.',
        image: '/parts/025035.webp',
        cta: 'View Part',
        onClick: () => navigate({ hash: '#part-025035' }),
      },
    ],
  };

  return (
    <ProductPage whereToBuy buyNow="B00033"
      {...productData}
      extraSections={<><UL101Technology /><AmmTaskFinder /></>}
      afterDetails={<UL101KitParts onRequestQuote={onRequestQuote} />}
      onRequestDemo={onRequestDemo}
      onRequestQuote={onRequestQuote}
    />
  );
};

export default UL101Page;
